import React, { useCallback, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { Checkbox, Input } from 'semantic-ui-react';

import { localeData } from '../../../locales/data';
import { addModifiers } from '../../../shared/helpers';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import analytics from '../../../shared/services/analytics/analytics';

type FilterType = 'recommendedReading' | 'reference' | 'tutorial' | 'videoCourse';

const FILTER_TYPE_LABELS: Record<FilterType, string> = {
  recommendedReading: 'learn.search.filter.only.recommendedReading',
  reference: 'learn.search.filter.only.reference',
  tutorial: 'learn.search.filter.only.tutorial',
  videoCourse: 'learn.search.filter.only.videoCourse',
};

interface InputSearchForm {
  queryTerm: string;
}

interface InputSearchProps {
  placeholder?: string;
  modifiers?: string[];
  defaultValue?: string;
  hasCheckboxFilter?: boolean;
  filterType?: FilterType;
  path: string;
}

const InputSearch = ({ placeholder, modifiers = [], defaultValue, hasCheckboxFilter, filterType, path }: InputSearchProps) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const [addFilter, setAddFilter] = useState(false);

  const queryFilters = useMemo(() => new URLSearchParams(location.search).getAll('filters[]'), [location.search]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<InputSearchForm>({
    defaultValues: { queryTerm: defaultValue ?? '' },
    mode: 'onChange',
  });

  const buildSearchParams = useCallback(
    (queryTerm: string): string => {
      const params = new URLSearchParams();
      params.set('query', queryTerm);

      const filters = addFilter && filterType ? [filterType] : queryFilters;
      filters.forEach(f => params.append('filters[]', f));

      return params.toString();
    },
    [addFilter, filterType, queryFilters]
  );

  const onSubmit = useCallback(
    ({ queryTerm }: InputSearchForm) => {
      if (!filterType) {
        analytics.trackCustomEventOnce('learn_search_global');
      }
      history.push({
        pathname: `/${path}/search`,
        search: buildSearchParams(queryTerm),
      });
    },
    [filterType, path, buildSearchParams, history]
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSubmit(onSubmit)();
      }
    },
    [handleSubmit, onSubmit]
  );

  const handleToggleCheckbox = useCallback(() => {
    if (filterType && ['tutorial', 'videoCourse'].includes(filterType)) {
      const eventName = filterType === 'tutorial' ? 'learn_toggle_tutorial_search_filter' : 'learn_toggle_course_search_filter';
      analytics.trackCustomEvent(eventName, {
        event_label: addFilter ? 'remove_filter' : 'add_filter',
      });
    }
    setAddFilter(prev => !prev);
  }, [filterType, addFilter]);

  const checkboxLabel = useMemo(() => {
    if (!filterType) return '';
    const labelId = FILTER_TYPE_LABELS[filterType];
    return intl.formatMessage({ id: labelId, defaultMessage: localeData.en[labelId] });
  }, [filterType, intl]);

  const queryTermError = errors.queryTerm?.message ?? '';

  return (
    <div className="learn__input-search-container">
      <div className={addModifiers('learn__input-search', modifiers)}>
        <div className="learn__input-search-content">
          <Controller
            name="queryTerm"
            control={control}
            rules={{ required: 'field.required' }}
            render={({ field: { onChange, value } }) => (
              <Input
                icon="search"
                name="queryTerm"
                placeholder={
                  placeholder ??
                  intl.formatMessage({
                    id: 'learnSearchBar.placeHolderSearch',
                    defaultMessage: localeData.en['learnSearchBar.placeHolderSearch'],
                  })
                }
                onKeyPress={handleKeyPress}
                onChange={(_: React.SyntheticEvent, data: { value: string }) => onChange(data.value)}
                value={value}
              />
            )}
          />
          <div className="learn__input-search-icon" onClick={handleSubmit(onSubmit)} />
        </div>
        <div className="error">
          {queryTermError !== '' ? (
            <FormattedMessageHelper tagName="span" id={queryTermError} defaultMessage={localeData.en[queryTermError]} />
          ) : null}
        </div>
      </div>
      {hasCheckboxFilter && filterType !== undefined ? (
        <div className="learn-search__checkbox">
          <Checkbox label={checkboxLabel} onChange={handleToggleCheckbox} checked={addFilter} />
        </div>
      ) : null}
    </div>
  );
};

export default InputSearch;
